/**
 * Helpers for surfacing API failures to the user.
 *
 * Historically a failed call just swapped a button's icon to a red "x" with no explanation, which
 * leaves the user with no idea what went wrong or whether retrying is worth it. Use these helpers
 * alongside the icon so there is always a readable reason.
 *
 * Errors carry a number (see `errorCodes.ts`) so a user can quote it to support and land on one
 * specific failure site. Warnings don't: something the user can see and fix for themselves — a date
 * in the past, an empty field — is not something anyone opens a ticket about, and a number on it is
 * just noise that makes a nudge look like a fault.
 *
 * Toasts go through `Xert.danger` (jQuery notify, see resources/assets/js/core.js), which renders
 * its message as HTML — so anything coming back from the server is escaped before being shown.
 */

/** Shape every backend endpoint that these helpers touch is expected to answer with. */
export type ErrorBearingResponse = {
  success?: boolean;
  message?: string;
  /** Some older endpoints answer with `error` instead of `message`. */
  error?: string;
  /**
   * The server's own number for this failure (a `2xxx` code). Deliberately not called `code`:
   * several of these endpoints pass a third party's error body through, and those carry a `code`
   * of their own that means something else entirely.
   */
  error_code?: number;
  /**
   * `'warning'` for outcomes the user can act on without our help, which are shown as a plain
   * message with no number. Anything else — including nothing at all — is treated as an error.
   */
  severity?: 'error' | 'warning';
};

/** A message to fall back on, and the number identifying the place it is shown from. */
export type ErrorFallback = {
  code: number;
  message: string;
};

/** Longer dwell than the 3s default: these carry information worth reading. */
const TOAST_TIMEOUT_MS = 8000;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Pulls a user-facing reason out of an API response, or `undefined` when the endpoint didn't say
 * anything useful (or said something clearly not meant for a user, such as a stack trace or an HTML
 * error page). Callers supply their own wording with `?? fallback`.
 */
export function errorMessageFrom(response: unknown): string | undefined {
  const candidate =
    typeof response === 'string'
      ? response
      : (response as ErrorBearingResponse)?.message ?? (response as ErrorBearingResponse)?.error;

  if (typeof candidate !== 'string') return undefined;

  const trimmed = candidate.trim();
  if (!trimmed) return undefined;
  // Guard against raw HTML error pages / dumped exceptions leaking into a toast.
  if (trimmed.length > 300 || trimmed.startsWith('<')) return undefined;

  return trimmed;
}

/** Pulls the server's error number out of a response, when it sent one. */
export function errorCodeFrom(response: unknown): number | undefined {
  const candidate = (response as ErrorBearingResponse)?.error_code;
  return Number.isInteger(candidate) && (candidate as number) > 0 ? (candidate as number) : undefined;
}

/**
 * Shows a red toast. `message` is plain text; it is escaped before display. `code` is the number
 * the user can quote back to us — see `errorCodes.ts`.
 */
export function showErrorToast(message: string, code: number): void {
  // @ts-ignore - Xert is a legacy global from resources/assets/js/core.js
  const Xert = window.Xert;
  if (!Xert?.danger) {
    console.error(`Error ${code}: ${message}`);
    return;
  }
  Xert.danger(`<strong>Error ${code}:</strong> ${escapeHtml(message)}`, null, TOAST_TIMEOUT_MS);
}

/**
 * Shows an amber toast for something the user can fix themselves — a date in the past, a field left
 * empty. No number: this isn't a fault to report, it's a nudge. `message` is plain text.
 */
export function showWarningToast(message: string): void {
  // @ts-ignore - Xert is a legacy global from resources/assets/js/core.js
  const Xert = window.Xert;
  if (!Xert?.warning) {
    console.warn(message);
    return;
  }
  Xert.warning(escapeHtml(message), null, TOAST_TIMEOUT_MS);
}

/**
 * Shows a toast explaining why an API call failed. Returns the message shown, so callers can reuse
 * it (e.g. for a tooltip) without re-deriving it.
 *
 * A response marked `severity: 'warning'` is shown as a plain message: the user can fix it
 * themselves, so there is nothing to quote at support. Everything else is an error and gets a
 * number. The message and the number are resolved independently — when the server explains a
 * failure it usually numbers it too, but where it only sends wording we keep `fallback.code`, which
 * still names the action that failed.
 */
export function notifyApiError(response: unknown, fallback: ErrorFallback): string {
  const message = errorMessageFrom(response) ?? fallback.message;

  if ((response as ErrorBearingResponse)?.severity === 'warning') {
    showWarningToast(message);
    return message;
  }

  const code = errorCodeFrom(response) ?? fallback.code;
  showErrorToast(message, code);
  return message;
}

/** Shows a green toast confirming something worked. `message` is plain text. */
export function showSuccessToast(message: string): void {
  // @ts-ignore - Xert is a legacy global from resources/assets/js/core.js
  const Xert = window.Xert;
  if (!Xert?.success) return;
  Xert.success(escapeHtml(message));
}
